import { useEffect, useMemo, useRef, useState } from "react";

import { useHistory, useLocation } from "react-router-dom";

import {
  Button,
  Link,
  Text,
  toastError,
  toastSuccess,
  toastWarning,
} from "@alpacahq/ui/old";

import { initAmplify } from "~/v2/api/auth/config";
import { UserActions } from "../../api/auth";
import { useMutationRequest } from "../../api/hooks";
import { CONTACT_URL, type SignUpLocationState } from ".";

export const getSearchParamsPreservingPlus = (search: string) =>
  new URLSearchParams(search.replace(/\+/g, "%2B"));

export const Pending = () => {
  const history = useHistory();
  const location = useLocation<SignUpLocationState>();
  const verificationAttemptedRef = useRef<string | null>(null);
  const [verificationFailed, setVerificationFailed] = useState(false);
  const urlParams = useMemo(
    () => getSearchParamsPreservingPlus(location.search),
    [location.search],
  );
  // Code and username come from the verification email link.
  const verificationUsername = urlParams.get("username") ?? "";
  const verificationCode = urlParams.get("code") ?? "";
  const verificationEmail = urlParams.get("email") ?? "";

  // Email and userSub come from CreateAccount.tsx or the verification link.
  const email = location.state?.email ?? verificationEmail;
  const userSub = location.state?.user_sub ?? verificationUsername;

  const { mutate: verify } = useMutationRequest(
    async () =>
      verificationCode &&
      verificationUsername &&
      (await UserActions.confirmUserSignUp(
        verificationUsername.toLowerCase(),
        verificationCode,
      )),
  );

  const { mutate: resend } = useMutationRequest(async () => {
    if (!userSub) {
      throw new Error("Email not found");
    }

    await UserActions.resendUserSignUp(userSub.toLowerCase());
  });

  useEffect(() => {
    initAmplify();

    if (!(verificationCode && verificationUsername)) {
      return;
    }

    const verificationKey = `${verificationUsername.toLowerCase()}:${verificationCode}`;
    if (verificationAttemptedRef.current === verificationKey) {
      return;
    }
    verificationAttemptedRef.current = verificationKey;

    verify(null, {
      onSuccess: () => {
        toastSuccess("Your email has been verified!");
        history.push("/signup/success");
      },
      onError: () => {
        setVerificationFailed(true);
      },
    });
  }, [history, verificationCode, verificationUsername, verify]);

  const onResend = () => {
    if (!userSub) {
      toastError(
        "We couldn't find the email address for this signup. Please sign up again or contact support.",
      );
      return;
    }

    resend(null, {
      onSuccess: () => {
        toastWarning(
          "Your verification code has resent. Please check your inbox.",
        );
      },
      onError: (error) => {
        toastError(error.message ?? "Error: could not resend verify email");
      },
    });
  };

  return (
    <div className="flex flex-col items-center justify-center gap-7">
      <h1 className="font-bold text-3xl">Verify your email</h1>

      <div className="flex flex-col items-center justify-center gap-2">
        <Text className="text-gray-600 text-lg">
          You're almost there! We sent an email{email ? " to" : "."}
        </Text>
        {email && (
          <Text className="font-medium text-2xl text-gray-900">{email}</Text>
        )}
      </div>

      {verificationFailed ? (
        <Text className="text-center text-gray-600 text-lg">
          Your verification link may have expired. Request a new verification
          email below, then use the latest link in your inbox.
        </Text>
      ) : (
        <Text className="text-center text-gray-600 text-lg">
          Click the link in the email to continue signup. If you don't see it,
          you may need to check your spam folder.
        </Text>
      )}

      <div className="flex flex-col items-center justify-center gap-4">
        <Text className="text-center text-gray-600 text-lg">
          Haven't received it?
        </Text>
        <Button onClick={onResend} type="button">
          Resend Verification Email
        </Button>
        <Text className="text-center text-gray-600">
          Need more help?{" "}
          <Link href={CONTACT_URL} rel="noopener">
            Contact support
          </Link>
        </Text>
      </div>
    </div>
  );
};
